import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, InstagramIcon, FacebookIcon, ExternalLinkIcon } from 'lucide-react';
// Custom TikTok Icon since it's not in standard lucide-react
const TikTokIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg" data-id="element-554">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-555" />
  </svg>;
const facebookLocations = [{
  name: 'Clinton',
  url: 'https://facebook.com/visualeyesclinton'
}, {
  name: 'Greenbelt',
  url: 'https://facebook.com/visualeyesgreenbelt'
}, {
  name: 'Hyattsville',
  url: 'https://facebook.com/visualeyeshyattsville'
}, {
  name: 'Mitchellville',
  url: 'https://facebook.com/visualeyesmitchellville'
}, {
  name: 'Silver Spring',
  url: 'https://facebook.com/visualeyessilverspring'
}, {
  name: 'Frederick',
  url: 'https://facebook.com/visualeyesfrederick'
}];
export function SocialMediaSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFacebookLocations, setShowFacebookLocations] = useState(false);
  // Marquee text items
  const marqueeItems = Array(10).fill('Follow us on social media');
  return <section className="relative bg-surface-muted overflow-hidden py-20 md:py-28" data-id="element-556">
      {/* Animated Marquee Strip */}
      <div className="absolute top-10 md:top-16 left-0 w-full bg-brand-gold py-3 md:py-4 transform -rotate-2 scale-110 z-10 shadow-md overflow-hidden" data-id="element-557">
        <div className="marquee-track flex whitespace-nowrap" data-id="element-558">
          {[0, 1].map(set => <div key={set} className="flex items-center shrink-0 marquee-content" data-id="element-559">
              {Array(8).fill(null).map((_, i) => <span key={i} className="text-white font-heading font-bold text-lg md:text-2xl uppercase tracking-wider mx-4 md:mx-8 inline-flex items-center gap-4 md:gap-8" data-id="element-560">
                    Follow us on social media
                    <span className="text-white/50 text-sm" data-id="element-561">✦</span>
                  </span>)}
            </div>)}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 mt-16 md:mt-24" data-id="element-562">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-563">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left order-2 lg:order-1" data-id="element-564">
            <h2 className="text-section-mobile md:text-section text-brand-dark font-heading font-bold mb-6" data-id="element-565">
              Join Our Community
            </h2>
            <p className="text-lg text-neutral-text/80 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-566">
              Stay connected with Visual Eyes Group! Follow us for eye health
              tips, exclusive promotions, behind-the-scenes clinic moments, and
              the latest eyewear trends.
            </p>
            <button onClick={() => setIsModalOpen(true)} className="bg-brand-dark text-white px-8 py-4 rounded-button font-heading font-semibold text-lg hover:bg-brand-gold transition-colors shadow-lg focus-ring inline-flex items-center gap-3" data-id="element-567">
              Connect With Us
              <InstagramIcon className="w-5 h-5" data-id="element-568" />
            </button>
          </motion.div>

          {/* Image Content */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative order-1 lg:order-2 flex justify-center items-end" data-id="element-569">
            {/* Decorative background blob */}
            <div className="absolute inset-0 bg-brand-gold-pale rounded-full blur-3xl opacity-60 transform scale-90" data-id="element-570" />

            <div className="relative z-10 w-full max-w-xs sm:max-w-sm md:max-w-lg mt-6 lg:mt-12 mb-0 lg:mb-[-120px]" data-id="element-571">
              <img src="https://cdn.magicpatterns.com/uploads/wTcYrtaxprcGKBp3g3Tz2q/ChatGPT_Image_Mar_11,_2026,_01_06_50_PM.png" alt="Phone showing Visual Eyes Instagram page" className="w-full drop-shadow-2xl lg:hidden" loading="lazy" data-id="element-572" />
              <img src="https://cdn.magicpatterns.com/uploads/wMCBibgjmLqQffX3ZTijkk/mockup.png" alt="Hand holding phone showing Visual Eyes Instagram page" className="w-full drop-shadow-2xl hidden lg:block" loading="lazy" data-id="element-573" />
            </div>

            {/* Floating social icons */}
            <motion.div animate={{
            y: [-10, 10, -10]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'easeInOut'
          }} className="absolute top-0 -right-2 md:-right-6 z-20 bg-white p-3 md:p-4 rounded-full shadow-xl" data-id="element-574">
              <InstagramIcon className="w-6 h-6 md:w-8 md:h-8 text-pink-500" data-id="element-575" />
            </motion.div>
            <motion.div animate={{
            y: [10, -10, 10]
          }} transition={{
            repeat: Infinity,
            duration: 5,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute bottom-16 md:bottom-24 -left-2 md:-left-6 z-20 bg-white p-3 md:p-4 rounded-full shadow-xl" data-id="element-576">
              <FacebookIcon className="w-6 h-6 md:w-8 md:h-8 text-blue-600" data-id="element-577" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Social Media Modal */}
      <AnimatePresence data-id="element-578">
        {isModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-brand-dark/60 backdrop-blur-sm" onClick={() => {
        setIsModalOpen(false);
        setShowFacebookLocations(false);
      }} data-id="element-579">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-card shadow-2xl w-full max-w-md overflow-hidden relative" onClick={e => e.stopPropagation()} data-id="element-580">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-surface-muted" data-id="element-581">
                <h3 className="font-heading font-bold text-xl text-brand-dark" data-id="element-582">
                  Follow Visual Eyes
                </h3>
                <button onClick={() => {
              setIsModalOpen(false);
              setShowFacebookLocations(false);
            }} className="p-2 text-neutral-text/50 hover:text-brand-dark transition-colors rounded-full hover:bg-gray-200" data-id="element-583">
                  <XIcon className="w-5 h-5" data-id="element-584" />
                </button>
              </div>

              <div className="p-6 space-y-4" data-id="element-585">
                {/* Instagram */}
                <a href="https://www.instagram.com/visualeyesgroup/" target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-pink-500 hover:bg-pink-50 transition-all group" data-id="element-586">
                  <div className="flex items-center gap-4" data-id="element-587">
                    <div className="bg-pink-100 p-3 rounded-full text-pink-600 group-hover:bg-pink-500 group-hover:text-white transition-colors" data-id="element-588">
                      <InstagramIcon className="w-6 h-6" data-id="element-589" />
                    </div>
                    <div data-id="element-590">
                      <h4 className="font-bold text-brand-dark" data-id="element-591">Instagram</h4>
                      <p className="text-sm text-neutral-text/60" data-id="element-592">
                        @visualeyes
                      </p>
                    </div>
                  </div>
                  <ExternalLinkIcon className="w-5 h-5 text-neutral-text/40 group-hover:text-pink-500" data-id="element-593" />
                </a>

                {/* X (Twitter) */}
                <a href="https://x.com/visualeyes4u" target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-neutral-800 hover:bg-gray-50 transition-all group" data-id="element-594">
                  <div className="flex items-center gap-4" data-id="element-595">
                    <div className="bg-gray-100 p-3 rounded-full text-neutral-800 group-hover:bg-neutral-800 group-hover:text-white transition-colors" data-id="element-596">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" data-id="element-597">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-598" />
                      </svg>
                    </div>
                    <div data-id="element-599">
                      <h4 className="font-bold text-brand-dark" data-id="element-600">X</h4>
                      <p className="text-sm text-neutral-text/60" data-id="element-601">
                        @visualeyes4u
                      </p>
                    </div>
                  </div>
                  <ExternalLinkIcon className="w-5 h-5 text-neutral-text/40 group-hover:text-neutral-800" data-id="element-602" />
                </a>

                {/* TikTok */}
                <a href="https://www.tiktok.com/@visualeyesgroup" target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-black hover:bg-gray-50 transition-all group" data-id="element-603">
                  <div className="flex items-center gap-4" data-id="element-604">
                    <div className="bg-gray-100 p-3 rounded-full text-black group-hover:bg-black group-hover:text-white transition-colors" data-id="element-605">
                      <TikTokIcon className="w-6 h-6" data-id="element-606" />
                    </div>
                    <div data-id="element-607">
                      <h4 className="font-bold text-brand-dark" data-id="element-608">TikTok</h4>
                      <p className="text-sm text-neutral-text/60" data-id="element-609">
                        @visualeyes
                      </p>
                    </div>
                  </div>
                  <ExternalLinkIcon className="w-5 h-5 text-neutral-text/40 group-hover:text-black" data-id="element-610" />
                </a>

                {/* Facebook (Expandable) */}
                <div className="border border-gray-200 rounded-xl overflow-hidden transition-all" data-id="element-611">
                  <button onClick={() => setShowFacebookLocations(!showFacebookLocations)} className="w-full flex items-center justify-between p-4 hover:bg-blue-50 transition-colors group" data-id="element-612">
                    <div className="flex items-center gap-4" data-id="element-613">
                      <div className="bg-blue-100 p-3 rounded-full text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-614">
                        <FacebookIcon className="w-6 h-6" data-id="element-615" />
                      </div>
                      <div className="text-left" data-id="element-616">
                        <h4 className="font-bold text-brand-dark" data-id="element-617">Facebook</h4>
                        <p className="text-sm text-neutral-text/60" data-id="element-618">
                          Select your local clinic
                        </p>
                      </div>
                    </div>
                    <motion.div animate={{
                  rotate: showFacebookLocations ? 180 : 0
                }} className="text-neutral-text/40 group-hover:text-blue-600" data-id="element-619">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-620">
                        <polyline points="6 9 12 15 18 9" data-id="element-621"></polyline>
                      </svg>
                    </motion.div>
                  </button>

                  <AnimatePresence data-id="element-622">
                    {showFacebookLocations && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-gray-50 border-t border-gray-200" data-id="element-623">
                        <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-624">
                          {facebookLocations.map(loc => <a key={loc.name} href={loc.url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 rounded-lg hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 transition-all group" data-id="element-625">
                              <span className="font-medium text-sm text-brand-dark" data-id="element-626">
                                {loc.name}
                              </span>
                              <ExternalLinkIcon className="w-3 h-3 text-neutral-text/40 group-hover:text-blue-600" data-id="element-627" />
                            </a>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}